import { useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'

import { SelectorButton } from '../../atomic/SelectorButton'
import { InitialPaymentInMonths } from './InitialPaymentInMonths'
import { Terms } from './Terms'
import styles from './profile-editor.module.scss'
import { Button, ButtonHierarchy } from '@/components/styleguide/Button'
import {
  constants,
  FILTER_LEASE_TYPES_BUSINESS,
  FILTER_LEASE_TYPES_PERSONAL,
  FILTER_STOCK_STATUS_FACTORY_ORDER,
  FILTER_STOCK_STATUS_IN_STOCK,
  FILTER_VEHICLE_TYPES_VANS,
  LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS,
  LEASE_PROFILE_FILTER_LEASE_TYPE,
  LEASE_PROFILE_FILTER_MILEAGES,
  LEASE_PROFILE_FILTER_MILEAGES_VALUES,
  LEASE_PROFILE_FILTER_STOCK_STATUS,
  LEASE_PROFILE_FILTER_TERMS,
} from '@/lib/constants'
import { useExperiment } from '@/lib/hooks/useExperiment'
import { formatWithCommas, getTermsAsYearsName } from '@/lib/utilities/search'
import { isProd } from '@/lib/utilities/system'
import { addToDataLayer, isLeaseProfileOptionAvailable } from '@/lib/utilities/vehicle'
import { postAnalyticsEvent } from '@/store/actions/event'
import { storeActiveLeaseProfile } from '@/store/reducers/leaseProfile'
import { selectAvailableLeaseProfileFilters } from '@/store/selectors/leaseProfile'
import { Size } from '@/types/system'

export const ProfileEditor = ({
  leaseProfile,
  vehicleType,
  setHasLeaseProfileChanged,
  openSelectorOverride,
  setOpenSelectorOverride,
  isConfig = false,
}) => {
  const dispatch = useDispatch()
  const [experimentGroup] = useExperiment()

  const availableFilters = useSelector(selectAvailableLeaseProfileFilters)
  const [openSelector, setOpenSelector] = useState('')
  const [term, setTerm] = useState(leaseProfile.term)
  const [mileage, setMileage] = useState(leaseProfile.mileage)
  const [leaseType, setLeaseType] = useState(leaseProfile.leaseType)
  const [stockStatus, setStockStatus] = useState(leaseProfile.stockStatus)
  const [initialPaymentInMonths, setInitialPaymentInMonths] = useState(
    leaseProfile.initialPaymentInMonths,
  )
  const isMobile = useMediaQuery({ maxWidth: 991 })
  const [version, setVersion] = useState('control')

  useEffect(() => {
    if (experimentGroup.startsWith('edit-lease-placement-variant')) {
      setVersion(experimentGroup.split('edit-lease-placement-')[1])
    }
  }, [experimentGroup])

  useEffect(() => {
    if (isMobile && (openSelector !== '' || openSelectorOverride !== '')) {
      setOpenSelector('')

      if (setOpenSelectorOverride) {
        setOpenSelectorOverride('')
      }
    }
  }, [isMobile, openSelector, openSelectorOverride, setOpenSelectorOverride])

  useEffect(() => {
    if (openSelector !== '') {
      addToDataLayer({
        event: 'modal-view',
        modal: '/profile-editor',
      })
    }
  }, [openSelector])

  useEffect(() => {
    if (leaseProfile) {
      setTerm(leaseProfile.term)
      setMileage(leaseProfile.mileage)
      setLeaseType(leaseProfile.leaseType)
      setStockStatus(leaseProfile.stockStatus)
      setInitialPaymentInMonths(leaseProfile.initialPaymentInMonths)
    }
  }, [leaseProfile])

  const newLeaseProfile = (filter, filterLabel) => {
    switch (filter) {
      case LEASE_PROFILE_FILTER_LEASE_TYPE:
        return {
          leaseType: filterLabel,
          term: leaseProfile.term,
          mileage: leaseProfile.mileage,
          initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
          stockStatus: leaseProfile.stockStatus,
          vehicleId: leaseProfile.vehicleId,
        }
      case LEASE_PROFILE_FILTER_TERMS:
        return {
          leaseType: leaseProfile.leaseType,
          term: filterLabel,
          mileage: leaseProfile.mileage,
          initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
          stockStatus: leaseProfile.stockStatus,
          vehicleId: leaseProfile.vehicleId,
        }
      case LEASE_PROFILE_FILTER_MILEAGES:
        return {
          leaseType: leaseProfile.leaseType,
          term: leaseProfile.term,
          mileage: filterLabel,
          initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
          stockStatus: leaseProfile.stockStatus,
          vehicleId: leaseProfile.vehicleId,
        }
      case LEASE_PROFILE_FILTER_INITIAL_PAYMENT_IN_MONTHS:
        return {
          leaseType: leaseProfile.leaseType,
          term: leaseProfile.term,
          mileage: leaseProfile.mileage,
          initialPaymentInMonths: filterLabel,
          stockStatus: leaseProfile.stockStatus,
          vehicleId: leaseProfile.vehicleId,
        }
      case LEASE_PROFILE_FILTER_STOCK_STATUS:
        return {
          leaseType: leaseProfile.leaseType,
          term: leaseProfile.term,
          mileage: leaseProfile.mileage,
          initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
          stockStatus: filterLabel,
          vehicleId: leaseProfile.vehicleId,
        }
      default:
        return {
          leaseType: leaseProfile.leaseType,
          term: leaseProfile.term,
          mileage: leaseProfile.mileage,
          initialPaymentInMonths: leaseProfile.initialPaymentInMonths,
          stockStatus: leaseProfile.stockStatus,
          vehicleId: leaseProfile.vehicleId,
        }
    }
  }

  const onChangeParameter = (filter, filterLabel) => {
    const profile = newLeaseProfile(filter, filterLabel)
    dispatch(storeActiveLeaseProfile(profile))
    const event = {
      event: 'lease-profile-filter',
      'filter-event': 'change-filter',
      'filter-category': filter,
      'filter-label': filterLabel,
      experimentGroup,
    }
    addToDataLayer(event)
    dispatch(
      postAnalyticsEvent({
        event: constants.eventTypes.leaseProfileFilter,
        payload: {
          'filter-event': 'change-filter',
          'filter-category': filter,
          'filter-label': filterLabel,
        },
      }),
    )
    if (setHasLeaseProfileChanged) {
      setHasLeaseProfileChanged(true)
    }
  }

  const closeSelector = () => {
    setOpenSelector('')
    if (setOpenSelectorOverride) {
      setOpenSelectorOverride('')
    }
  }

  const onChangeMileage = (filter, filterLabel) => {
    setMileage(filterLabel)
    onChangeParameter(filter, filterLabel)
  }

  const onChangeTerm = (filter, filterLabel) => {
    setTerm(filterLabel)
    onChangeParameter(filter, filterLabel)
  }

  const onChangeInitialPayment = (filter, filterLabel) => {
    setInitialPaymentInMonths(filterLabel)
    onChangeParameter(filter, filterLabel)
  }

  const onToggleSelector = type => {
    if (openSelector === type || openSelectorOverride === type) {
      setOpenSelector('')
      if (setOpenSelectorOverride) {
        setOpenSelectorOverride('')
      }
      document.querySelector('header').classList.remove('overlay-open')
    } else {
      setOpenSelector(type)
      if (setOpenSelectorOverride) {
        setOpenSelectorOverride(type)
      }
      document.querySelector('header').classList.add('overlay-open')
    }
  }

  const onToggleEdit = () => {
    if (openSelector || openSelectorOverride) {
      setOpenSelector('')
      if (setOpenSelectorOverride) {
        setOpenSelectorOverride('')
      }
      document.querySelector('header').classList.remove('overlay-open')
      addToDataLayer({
        event: 'lease-profile-filter',
        'filter-event': 'close',
        experimentGroup,
      })
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.leaseProfileFilter,
          payload: {
            'filter-event': 'close',
          },
        }),
      )
    } else {
      setOpenSelector(LEASE_PROFILE_FILTER_TERMS)
      if (setOpenSelectorOverride) {
        setOpenSelectorOverride(LEASE_PROFILE_FILTER_TERMS)
      }
      document.querySelector('header').classList.add('overlay-open')
      addToDataLayer({
        event: 'lease-profile-filter',
        'filter-event': 'open',
        experimentGroup,
      })
      dispatch(
        postAnalyticsEvent({
          event: constants.eventTypes.leaseProfileFilter,
          payload: {
            'filter-event': 'open',
          },
        }),
      )
    }
  }

  const masterOpenSelector = isConfig ? openSelectorOverride : openSelector

  return (
    <>
      <div
        className={`${styles.overlay} ${masterOpenSelector === '' ? styles.closed : styles.open}`}
        onClick={closeSelector}></div>
      <div
        className={`${styles.wrapper} ${version !== 'control' ? styles.variant : ''} ${masterOpenSelector === '' ? styles.closed : styles.open}`}>
        <div className={`${styles['button-group']} ${styles['top-level']}`}>
          <button
            type="button"
            className={`${styles['toggle-button']} ${
              masterOpenSelector === LEASE_PROFILE_FILTER_TERMS ? styles.active : ''
            }`}
            onClick={() => onToggleSelector(LEASE_PROFILE_FILTER_TERMS)}>
            {`${getTermsAsYearsName(leaseProfile.term)} yr${
              leaseProfile.term !== 12 ? 's' : ''
            } + ${leaseProfile.initialPaymentInMonths} mo${
              leaseProfile.initialPaymentInMonths > 1 ? 's' : ''
            } upfront`}
            <span>
              , {leaseProfile.mileage / 1000}k miles,
              {leaseProfile.leaseType === FILTER_LEASE_TYPES_BUSINESS ? 'Business' : 'Personal'},
              {leaseProfile.stockStatus === FILTER_STOCK_STATUS_IN_STOCK
                ? ' In stock'
                : ' Factory order'}
            </span>
          </button>
          <div
            className={`${styles['vertical-line']} ${
              masterOpenSelector === LEASE_PROFILE_FILTER_MILEAGES ||
              masterOpenSelector === LEASE_PROFILE_FILTER_TERMS
                ? styles.white
                : ''
            }`}></div>
          <button
            type="button"
            className={`${styles['toggle-button']} ${
              masterOpenSelector === LEASE_PROFILE_FILTER_MILEAGES ? styles.active : ''
            }`}
            onClick={() => onToggleSelector(LEASE_PROFILE_FILTER_MILEAGES)}>
            {formatWithCommas(leaseProfile.mileage)} miles
          </button>
          <div
            className={`${styles['vertical-line']} ${
              masterOpenSelector === LEASE_PROFILE_FILTER_STOCK_STATUS ||
              masterOpenSelector === LEASE_PROFILE_FILTER_MILEAGES
                ? styles.white
                : ''
            }`}></div>
          <button
            type="button"
            className={`${styles['toggle-button']} ${
              masterOpenSelector === LEASE_PROFILE_FILTER_STOCK_STATUS ? styles.active : ''
            }`}
            onClick={() => onToggleSelector(LEASE_PROFILE_FILTER_STOCK_STATUS)}>
            {leaseProfile.leaseType === FILTER_LEASE_TYPES_BUSINESS ? 'Business' : 'Personal'},
            {leaseProfile.stockStatus === FILTER_STOCK_STATUS_IN_STOCK
              ? ' In stock'
              : ' Factory order'}
          </button>
          <Button
            className={`${styles['edit-button']}${!isProd() ? ' profile-editor__edit-button' : ''}`}
            onClick={onToggleEdit}
            label={masterOpenSelector ? 'Close' : 'Edit'}
            icon={masterOpenSelector ? 'X' : 'SlidersHorizontal'}
            size={Size.Small}
            hierarchy={ButtonHierarchy.Filled}
          />
        </div>
        <div className={styles['dropdown-wrapper']}>
          <AnimateHeight
            id="lease-profile-type"
            duration={300}
            height={masterOpenSelector === LEASE_PROFILE_FILTER_STOCK_STATUS ? 'auto' : 0}
            delay={masterOpenSelector === LEASE_PROFILE_FILTER_STOCK_STATUS ? 450 : 0}
            animateOpacity={true}>
            <div className={styles.dropdown}>
              <div className={styles.inner}>
                <h3>Type of lease</h3>
                <div className={`${styles['button-group']} ${styles['connected-buttons']}`}>
                  <button
                    type="button"
                    className={`${leaseType === FILTER_LEASE_TYPES_PERSONAL ? styles.active : ''}${!isProd() ? ' profile-editor__lease-type-button--personal' : ''}`}
                    onClick={() => {
                      setLeaseType(FILTER_LEASE_TYPES_PERSONAL)
                      onChangeParameter(
                        LEASE_PROFILE_FILTER_LEASE_TYPE,
                        FILTER_LEASE_TYPES_PERSONAL,
                      )
                    }}
                    disabled={
                      !isLeaseProfileOptionAvailable(
                        availableFilters,
                        'leaseTypes',
                        FILTER_LEASE_TYPES_PERSONAL,
                      )
                    }>
                    <p>Personal</p>
                    <p>
                      <span>Incl VAT</span>
                    </p>
                  </button>
                  <button
                    type="button"
                    className={`${leaseType === FILTER_LEASE_TYPES_BUSINESS ? styles.active : ''}${!isProd() ? ' profile-editor__lease-type-button--business' : ''}`}
                    onClick={() => {
                      setLeaseType(FILTER_LEASE_TYPES_BUSINESS)
                      onChangeParameter(
                        LEASE_PROFILE_FILTER_LEASE_TYPE,
                        FILTER_LEASE_TYPES_BUSINESS,
                      )
                    }}
                    disabled={
                      !isLeaseProfileOptionAvailable(
                        availableFilters,
                        'leaseTypes',
                        FILTER_LEASE_TYPES_BUSINESS,
                      )
                    }>
                    <p>Business</p>
                    <p>
                      <span>Excl VAT</span>
                    </p>
                  </button>
                </div>
                <p className={styles.paragraph}>
                  {leaseType === FILTER_LEASE_TYPES_BUSINESS
                    ? `Lease your next company ${
                        vehicleType === FILTER_VEHICLE_TYPES_VANS ? 'van' : 'car'
                      }. All deals come with manufacturer's warranty, road tax and exclude VAT.`
                    : `Lease your next ${
                        vehicleType === FILTER_VEHICLE_TYPES_VANS ? 'van' : 'car'
                      } for yourself. All deals come with manufacturer's warranty and road tax as standard.`}
                </p>
                <hr />
                <h3>Stock status</h3>
                <div className={`${styles['button-group']} ${styles.padding}`}>
                  <SelectorButton
                    onChangeParameter={() => {
                      setStockStatus(FILTER_STOCK_STATUS_IN_STOCK)
                      onChangeParameter(
                        LEASE_PROFILE_FILTER_STOCK_STATUS,
                        FILTER_STOCK_STATUS_IN_STOCK,
                      )
                    }}
                    filter={LEASE_PROFILE_FILTER_STOCK_STATUS}
                    filterValue={FILTER_STOCK_STATUS_IN_STOCK}
                    activeValue={stockStatus}
                    availableFilters={availableFilters}
                  />
                  <SelectorButton
                    onChangeParameter={() => {
                      setStockStatus(FILTER_STOCK_STATUS_FACTORY_ORDER)
                      onChangeParameter(
                        LEASE_PROFILE_FILTER_STOCK_STATUS,
                        FILTER_STOCK_STATUS_FACTORY_ORDER,
                      )
                    }}
                    filter={LEASE_PROFILE_FILTER_STOCK_STATUS}
                    filterValue={FILTER_STOCK_STATUS_FACTORY_ORDER}
                    activeValue={stockStatus}
                    availableFilters={availableFilters}
                  />
                </div>
              </div>
            </div>
          </AnimateHeight>
          <AnimateHeight
            id="lease-profile-price"
            duration={300}
            delay={masterOpenSelector === LEASE_PROFILE_FILTER_TERMS ? 450 : 0}
            height={masterOpenSelector === LEASE_PROFILE_FILTER_TERMS ? 'auto' : 0}
            animateOpacity={true}>
            <div className={styles.dropdown}>
              <div className={styles.inner}>
                <Terms
                  term={term}
                  onChangeTerm={onChangeTerm}
                  availableFilters={availableFilters}
                />
                <hr />
                <InitialPaymentInMonths
                  initialPaymentInMonths={initialPaymentInMonths}
                  onChangeInitialPayment={onChangeInitialPayment}
                  availableFilters={availableFilters}
                />
                <p className={styles.paragraph}>
                  Your upfront payment is your first month&apos;s payment. The higher your upfront
                  payment the cheaper your monthly payments will be.
                </p>
              </div>
            </div>
          </AnimateHeight>
          <AnimateHeight
            id="lease-profile-mileage"
            duration={300}
            delay={masterOpenSelector === LEASE_PROFILE_FILTER_MILEAGES ? 450 : 0}
            height={masterOpenSelector === LEASE_PROFILE_FILTER_MILEAGES ? 'auto' : 0}
            animateOpacity={true}>
            <div className={styles.dropdown}>
              <div className={styles.inner}>
                <h3>Miles per annum</h3>
                <div className={`${styles['button-group']} ${styles.stacked}`}>
                  {LEASE_PROFILE_FILTER_MILEAGES_VALUES.map(value => {
                    return (
                      <SelectorButton
                        key={value}
                        onChangeParameter={onChangeMileage}
                        filter={LEASE_PROFILE_FILTER_MILEAGES}
                        filterValue={value}
                        activeValue={mileage}
                        availableFilters={availableFilters}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          </AnimateHeight>
        </div>
      </div>
    </>
  )
}
